import { ReactComponent as SvgGoogle } from "assets/google-logo.svg";
import CTAButton from "components/CTAButton";
import { useEnterWithEmail } from "hooks/useEnterWithEmail";
import { useEnterWithGoogle } from "hooks/useEnterWithGoogle";
import { validate as isEmail } from "isemail";
import React, { ChangeEvent, SyntheticEvent, useRef, useState } from "react";
import styled from "styled-components/macro";
import { mediaMin } from "styles/media";

const SignUpInputWrapper = styled.div`
  display: flex;
  max-width: 460px;
  margin: 0 auto;
  justify-content: space-between;
  gap: 8px;
  padding: 4px;

  border-radius: 100px;
  background: ${({ theme }) => theme.palette.gray200};

  ${mediaMin.xs`
    padding: 6px;
  `}
`;

const SingleSignOnWrapper = styled.div`
  display: flex;
  height: 34px;
  max-width: 460px;
  margin: 12px auto;
  align-items: center;
  justify-content: center;
`;

const SingleSignOnText = styled.span`
  color: ${({ theme }) => theme.palette.gray200};
  font-size: 12px;
  font-weight: 500;
`;

const GoogleButton = styled.button`
  display: flex;
  gap: 8px;
  align-items: center;
  margin: 0 0 0 8px;
  height: 34px;
  border-radius: 17px;
  padding: 0 10px;

  svg {
    width: 20px;
    height: 20px;
  }

  &:hover {
    background-color: ${({ theme }) => theme.palette.white10};
  }
`;

const Form = styled.form`
  display: flex;
  flex: 1;
  position: relative;
`;

export const SubmitButton = styled(CTAButton)`
  padding: 0 18px;
  height: 40px;
  font-size: 13px;

  ${mediaMin.xs`
    padding: 0 20px;
    height: 44px;
    font-size: 15px;
  `}
`;

const Input = styled.input`
  flex: 1;
  width: 100%;
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.palette.black};
  padding: 0 0 0 12px;

  &::placeholder {
    color: ${({ theme }) => theme.palette.gray600};
  }

  ${mediaMin.xs`
    font-size: 14px;
    padding: 0 0 0 18px;
  `}
`;

const InvalidMessage = styled.span`
  position: absolute;
  bottom: -24px;
  font-size: 12px;
`;

export default function SignUpInput({
  formId = "sign-up-form",
}: {
  formId?: string;
}) {
  const [inputValue, setInputValue] = useState("");
  const [isInvalidEmail, setIsInvalidEmail] = useState(false);
  const isSubmitting = useRef(false);
  const handleClickGoogleButton = useEnterWithGoogle();
  const { handleSignup } = useEnterWithEmail(inputValue);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
    setIsInvalidEmail(false);
  };

  const handleSubmitSignUp = async (e: SyntheticEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!isSubmitting.current) {
      isSubmitting.current = true;
      if (isEmail(inputValue)) {
        await handleSignup();
      }
      isSubmitting.current = false;
    }
  };

  return (
    <>
      <SignUpInputWrapper>
        <Form id={formId} noValidate onSubmit={handleSubmitSignUp}>
          <Input
            type="email"
            value={inputValue}
            onChange={handleChange}
            placeholder="Enter your email"
          />
          {isInvalidEmail && (
            <InvalidMessage>Please check your email.</InvalidMessage>
          )}
        </Form>
        <SubmitButton form={formId}>Get started for free</SubmitButton>
      </SignUpInputWrapper>
      <SingleSignOnWrapper>
        <SingleSignOnText>Or start with </SingleSignOnText>
        <GoogleButton onClick={handleClickGoogleButton}>
          <SvgGoogle />
          <SingleSignOnText>Google</SingleSignOnText>
        </GoogleButton>
      </SingleSignOnWrapper>
    </>
  );
}
